import { inject } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ENVIRONMENT, environmentGuard, loginGuard } from '@cfp/core';

/**
 * The convention for naming the webpack chunk important: [environmentCode].[moduleName]
 * When creating the distribution for an environment (like manager, breeam, etc) all chunks
 * that does not contain the "environmentCode" will not be included in the distribution and
 * thus also not available for that environment to be loaded. In this way we can avoid that
 * customer specific code is accidental available for other customers.
 *
 * Common modules that can be used for all customer (like energy, relation management, etc)
 * should not have an environmentCode prefix.
 */

export const routes: Routes = [
  {
    path: 'energy',
    loadChildren: () => import('@cfp/features/energy-routing').then((m) => m.EnergyRoutingModule)
  },
  {
    path: 'breeam',
    loadChildren: () => import('@cfp/features/breeam').then((m) => m.BreeamRoutingModule)
  },
  {
    path: 'create-account',
    canMatch: [() => inject(ENVIRONMENT).loginAllowSelfOnboarding === true],
    loadChildren: () => import('@cfp/features-shared/self-onboarding').then((m) => m.SelfOnboardingModule)
  },
  {
    path: 'best-value',
    loadChildren: () =>
      import('./modules/klimaatroute/best-value/app-klimaatroute-best-value-module').then(
        (m) => m.BestValueModuleWithRouting
      )
  },
  {
    path: 'form',
    loadChildren: () =>
      import('./modules/kbc/sustainable-building-scan/app-kbc-sustainable-building-scan-module').then(
        (m) => m.SustainableBuildingScanModuleWithRouting
      )
  },
  {
    path: 'scan',
    loadChildren: () =>
      import('./modules/rabobank/scan/app-rabobank-scan-module').then((m) => m.RabobankScanModuleWithRouting)
  },
  // ING Ref
  {
    path: 'digital-channels',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/ref');
      }
    ],
    loadChildren: () =>
      import('./modules/ref/digital-channels/app-ref-digital-channels-module').then((m) => m.DigitalChannelsModule)
  },
  {
    path: 'ing-digital-channels',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/ref');
      }
    ],
    loadChildren: () =>
      import('./modules/ref/digital-channels/app-ref-digital-channels-module').then((m) => m.DigitalChannelsModule)
  },
  {
    path: 'green-financing',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/ref');
      }
    ],
    loadChildren: () =>
      import('./modules/ref/green-financing/app-ref-green-financing-module').then(
        (m) => m.GreenFinancingModuleWithRouting
      ),
    data: { permission: 'greenFinancing' },
    canActivate: [environmentGuard]
  },
  // ING PL
  {
    path: 'kalkulator',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/ing-pl');
      }
    ],
    loadChildren: () =>
      import('./modules/ing-pl/kalkulator/app-ing-pl-kalkulator-module').then((m) => m.IngPlKalkulatorModule)
  },
  // HSBC Retail
  {
    path: '',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/hsbc-retail');
      }
    ],
    loadChildren: () =>
      import('./modules/hsbc-retail/heec/app-hsbc-retail-heec-module').then((m) => m.HsbcRetailHeecModule)
  },
  // HSBC Global Pro
  {
    path: 'cookie-notice',
    canMatch: [() => window.location.pathname.startsWith('/csr/app/hsbc-global-pro')],
    loadChildren: () =>
      import('./modules/hsbc-shared/cookie-notice/app-hsbc-global-pro-cookie-notice-module').then(
        (m) => m.HsbcGlobalProCookieNoticeModule
      )
  },
  // HSBC Global
  {
    path: '',
    canMatch: [
      () => {
        return (
          window.location.pathname.startsWith('/csr/app/hsbc-global') &&
          !window.location.pathname.startsWith('/csr/app/hsbc-global-pro')
        );
      }
    ],
    loadChildren: () =>
      import('./modules/hsbc-shared/bsat-lite/app-hsbc-global-bsat-lite-module').then((m) => m.HsbcGlobalBsatLiteModule)
  },
  {
    path: 'cookie-notice',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/hsbc-global');
      }
    ],
    loadChildren: () =>
      import('./modules/hsbc-shared/cookie-notice/app-hsbc-global-cookie-notice-module').then(
        (m) => m.HsbcGlobalCookieNoticeModule
      )
  },
  // HSBC
  {
    path: 'lite',
    canMatch: [
      () => {
        return (
          window.location.pathname.startsWith('/csr/app/hsbc') &&
          !window.location.pathname.startsWith('/csr/app/hsbc-global') &&
          !window.location.pathname.startsWith('/csr/app/hsbc-global-pro')
        );
      }
    ],
    data: { baseUrl: 'lite' },
    loadChildren: () =>
      import('./modules/hsbc-shared/bsat-lite/app-hsbc-bsat-lite-module').then((m) => m.HsbcBsatLiteModule)
  },
  {
    path: 'cookie-notice',
    canMatch: [
      () => {
        return (
          window.location.pathname.startsWith('/csr/app/hsbc') &&
          !window.location.pathname.startsWith('/csr/app/hsbc-global') &&
          !window.location.pathname.startsWith('/csr/app/hsbc-global-pro')
        );
      }
    ],
    loadChildren: () =>
      import('./modules/hsbc-shared/cookie-notice/app-hsbc-cookie-notice-module').then((m) => m.HsbcCookieNoticeModule)
  },
  // Rabobank
  {
    path: '',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/rabobank');
      }
    ],
    loadChildren: () => import('./modules/rabobank/start/app-rabobank-start-module').then((m) => m.RabobankStartModule)
  },
  {
    path: 'relation-management/clients/create',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/rabobank/relation-management');
      }
    ],
    data: { module: 'relation-management' },
    loadChildren: () =>
      import('./modules/rabobank/relation-management/app-rabobank-relation-management-module').then(
        (m) => m.RabobankRelationManagementModule
      )
  },
  // Piraeus
  {
    path: 'lite',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/piraeus');
      }
    ],
    canActivate: [loginGuard],
    loadChildren: () => import('@cfp/features/lite/lite.module').then((m) => m.LiteModule)
  },
  {
    path: 'piraeus',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/piraeus');
      }
    ],
    loadChildren: () => import('./modules/piraeus/app-piraeus-module').then((m) => m.PiraeusModule)
  },
  // Sport NL Groen
  {
    path: '',
    canMatch: [
      () => {
        return window.location.pathname.startsWith('/csr/app/sportnlgroen');
      }
    ],
    canActivate: [loginGuard],
    loadChildren: () => import('./modules/sportnlgroen/app-sportnlgroen-module').then((m) => m.SportNlGroenModule)
  },
  // Below module will only be available for development showing a page with all shared applications
  {
    path: '',
    canMatch: [
      () => {
        return (
          window.location.href === 'http://localhost:8002/csr/app/' ||
          window.location.href === 'http://localhost:8002/csr/app'
        );
      }
    ],
    loadChildren: () =>
      import('./modules/shared/shared-landing-page/app-shared-landing-page-module').then(
        (m) => m.SharedLandingPageModuleWithRouting
      )
  }
];

export const SharedAppRouting = RouterModule.forRoot(routes, {});
